export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  linkedIn: string;
  imageQuery: string;
  department: 'executive' | 'engineering' | 'talent' | 'operations';
  location: string;
}

export const teamMembers: TeamMember[] = [
  {
    id: 'marcus-reid',
    name: 'Marcus Reid',
    role: 'Co-Founder & Chief Technology Officer',
    bio: 'Marcus co-founded Thoughtic Solutions in 2019 after leading platform engineering at two fintech unicorns. He holds a PhD in Distributed Systems from MIT and has architected systems processing over $2B in annual transactions. At Thoughtic, he oversees all technical delivery, from cloud infrastructure to ML pipelines, and personally vets every senior engineer who joins the bench. He is obsessed with latency — "If a query takes more than 50ms, we are having a conversation."',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of a confident male tech executive in his mid-30s wearing a navy blazer over a light grey shirt, warm studio lighting, neutral grey background, editorial business photography style, sharp and modern aesthetic',
    department: 'executive',
    location: 'San Francisco, CA',
  },
  {
    id: 'elena-voss',
    name: 'Elena Voss',
    role: 'Co-Founder & Chief Executive Officer',
    bio: 'Elena built Thoughtic\'s go-to-market engine from zero to $15M ARR in four years. Previously VP of Sales at a leading talent marketplace, she understood that engineering staffing was broken — slow, expensive, and opaque. She founded Thoughtic to fix it. Elena holds an MBA from Stanford and speaks regularly at SaaStr and SaaS Monster. She runs the company with one rule: "Every client decision should pass the 48-hour test — if we cannot deliver value in 48 hours, we do not take the engagement."',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of a confident female business executive in her early 40s wearing an elegant charcoal blazer, warm natural lighting, clean white studio background, editorial corporate photography style, approachable and authoritative expression',
    department: 'executive',
    location: 'New York, NY',
  },
  {
    id: 'james-thornton',
    name: 'James Thornton',
    role: 'Chief Operating Officer',
    bio: 'James runs the operational backbone of Thoughtic — from talent matching algorithms to client success metrics. Previously Director of Operations at a top-tier consulting firm, he brought rigorous process discipline to a traditionally chaotic industry. He designed the 14-day onboarding framework, the 48-hour matching SLA, and the real-time talent dashboard that clients now depend on. James holds a degree in Industrial Engineering from Georgia Tech and is a certified Six Sigma Black Belt.',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of a male operations executive in his late 30s wearing a dark suit with an open collar, confident posture, modern office background slightly blurred, corporate editorial photography style, warm and professional aesthetic',
    department: 'executive',
    location: 'Austin, TX',
  },
  {
    id: 'priya-sharma',
    name: 'Priya Sharma',
    role: 'VP of Engineering Delivery',
    bio: 'Priya leads a delivery organization of 45 engineering leads and solution architects across 8 time zones. Before Thoughtic, she spent 7 years at Google leading the Chrome Performance team, where she reduced rendering latency by 34% across 2 billion devices. She brings that same systems-thinking to client engagements — every project gets a dedicated delivery lead, a technical architecture review, and a weekly client sync. Priya holds an MS in Computer Science from IIT Bombay.',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of a South Asian female engineering leader in her mid-30s wearing a modern teal blouse, bright and confident expression, clean studio background with soft lighting, editorial tech industry photography style, warm and modern aesthetic',
    department: 'engineering',
    location: 'Bangalore, India',
  },
  {
    id: 'derek-ngo',
    name: 'Derek Ngo',
    role: 'Principal Cloud Architect',
    bio: 'Derek is the architect behind Thoughtic\'s multi-cloud infrastructure platform, which supports 400+ client environments across AWS, Azure, and GCP. He designed the Terraform module system, the drift-detection pipeline, and the cost-optimization framework that saves clients an average of $20K/month. Before Thoughtic, Derek was a Senior Solutions Architect at AWS, where he advised Fortune 500 companies on cloud migration strategy. He holds 3 AWS certifications and speaks at re:Invent annually.',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of an Asian male cloud architect in his early 40s wearing glasses and a dark henley shirt under a blazer, thoughtful expression, modern tech office background with glass walls slightly blurred, editorial corporate photography style',
    department: 'engineering',
    location: 'Seattle, WA',
  },
  {
    id: 'sarah-lin',
    name: 'Dr. Sarah Lin',
    role: 'Principal Solutions Architect',
    bio: 'Sarah is Thoughtic\'s lead architect for healthtech and fintech engagements, where domain expertise is as critical as technical skill. She holds a PhD in Biomedical Informatics from Johns Hopkins and spent 5 years at Epic Systems designing HIPAA-compliant data pipelines for 400+ hospital networks. At Thoughtic, she architects systems that pass audits on the first try — because she designs compliance into the data model, not as an afterthought. She leads our security review board and mentors junior architects.',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of an Asian female solutions architect in her mid-30s wearing a white blouse and navy cardigan, warm studio lighting, soft grey background, editorial healthcare-tech photography style, intelligent and approachable expression',
    department: 'engineering',
    location: 'Boston, MA',
  },
  {
    id: 'catherine-moore',
    name: 'Catherine Moore',
    role: 'VP of Talent & Client Success',
    bio: 'Catherine runs the largest team at Thoughtic: 32 talent specialists, client success managers, and technical recruiters who collectively manage 2,400+ vetted engineers on the bench. She built the 7-stage vetting pipeline, the skill-matching algorithm, and the quarterly client health-score system that maintains a 94% retention rate. Before Thoughtic, she was Head of Talent at a top European SaaS company, where she hired 200 engineers in 18 months. She speaks fluent English, French, and German.',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of a female talent executive in her early 40s wearing a burgundy blazer, confident smile, modern coworking space background with warm lighting, editorial HR leadership photography style, approachable and professional aesthetic',
    department: 'talent',
    location: 'London, UK',
  },
  {
    id: 'tom-whitfield',
    name: 'Tom Whitfield',
    role: 'Head of Engineering Excellence',
    bio: 'Tom defines the standards that every Thoughtic engineer must meet — from code review guidelines to architecture decision records. He built the AI-assisted code review pipeline, the automated testing framework, and the engineering excellence scorecard that clients see in every weekly report. Previously a Staff Engineer at Spotify, he contributed to the open-source Backstage project and maintains a popular TypeScript performance blog with 50K monthly readers. He believes great code is boring code — predictable, readable, and well-tested.',
    linkedIn: '#',
    imageQuery:
      'Professional portrait of a male software engineering leader in his mid-30s with a beard, wearing a casual olive button-down shirt, creative tech office background with plants and warm lighting, editorial startup photography style, authentic and modern aesthetic',
    department: 'engineering',
    location: 'Berlin, Germany',
  },
];

export const teamDepartments = [
  { key: 'all', label: 'All Leadership' },
  { key: 'executive', label: 'Executive Team' },
  { key: 'engineering', label: 'Engineering' },
  { key: 'talent', label: 'Talent & Operations' },
];